import React, { useEffect, useMemo } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  UncontrolledDropdown,
} from "reactstrap";
import classnames from "classnames";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import {
  LeaveApplicationStatus,
  leaveApplicationStatusMap,
} from "../constants/leave-application.constants";
import {
  leaveApplicationEmployeeLabel,
  leaveTypeLabel,
} from "../utils/leaveApplicationDisplay";
import { useLeaveApplicationOverview } from "./LeaveApplicationOverviewContext";
import OverviewTab from "./OverviewTab";
import {
  LeaveApplicationOverviewTabParam,
  isValidLeaveApplicationOverviewTabParam,
  leaveApplicationOverviewTabIdFromSearchParam,
  searchParamForLeaveApplicationOverviewTabId,
} from "./overviewTabSearchParams";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import {
  LeaveApplicationActionModals,
  type LeaveApplicationAction,
} from "../components/LeaveApplicationActionModals";

const Section = () => {
  const { record, refetch } = useLeaveApplicationOverview();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = useAccess();
  const canApprove = can(Permissions.LEAVE_APPLICATION.APPROVE);
  const canReject = can(Permissions.LEAVE_APPLICATION.REJECT);
  const canDelete = can(Permissions.LEAVE_APPLICATION.DELETE);
  const canCancel = can(Permissions.LEAVE_APPLICATION.CANCEL);
  const [action, setAction] = React.useState<LeaveApplicationAction | null>(null);

  const tabQuery = searchParams.get("tab");

  useEffect(() => {
    if (tabQuery && !isValidLeaveApplicationOverviewTabParam(tabQuery)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          return next;
        },
        { replace: true },
      );
    }
  }, [tabQuery, setSearchParams]);

  const pending = record?.status === LeaveApplicationStatus.PENDING;
  const approved = record?.status === LeaveApplicationStatus.APPROVED;

  const activeTab = leaveApplicationOverviewTabIdFromSearchParam(tabQuery);

  const setTabInUrl = (tabId: string) => {
    const param = searchParamForLeaveApplicationOverviewTabId(tabId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!param || param === LeaveApplicationOverviewTabParam.OVERVIEW) {
          next.delete("tab");
        } else {
          next.set("tab", param);
        }
        return next;
      },
      { replace: true },
    );
  };

  const displayTitle = useMemo(() => {
    if (!record) return "—";
    return leaveApplicationEmployeeLabel(record);
  }, [record]);

  const subtitle = useMemo(() => {
    if (!record) return null;
    const lt = leaveTypeLabel(record);
    return lt && lt !== "—" ? lt : null;
  }, [record]);

  if (!record) {
    return (
      <Row>
        <Col lg={12}>
          <p className="text-muted mb-0">
            This leave application could not be loaded.
          </p>
        </Col>
      </Row>
    );
  }

  return (
    <React.Fragment>
      <LeaveApplicationActionModals
        record={record}
        action={action}
        onClose={() => setAction(null)}
        onSuccess={(act) => {
          setAction(null);
          if (act === "delete") {
            navigate("/hr-management/leave-applications");
          } else {
            void refetch();
          }
        }}
      />

      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3">
                  <div className="col-md">
                    <div>
                      <h4 className="fw-bold">{displayTitle}</h4>
                      {subtitle ? (
                        <p className="text-muted mb-1">
                          <span className="fw-medium">{subtitle}</span>
                        </p>
                      ) : null}
                      <div className="hstack gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted">Status:</span>
                          <StatusBadge
                            value={record.status}
                            map={leaveApplicationStatusMap}
                          />
                        </div>
                        {record.created_at ? (
                          <>
                            <div className="vr" />
                            <div>
                              Created:{" "}
                              <span className="fw-medium">
                                <DateTime date={record.created_at} />
                              </span>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  {(pending && (canApprove || canReject || canDelete)) ||
                  (approved && canCancel) ? (
                    <div className="col-md-auto mt-2 mt-md-0">
                      <div className="hstack gap-2">
                        {pending && canApprove ? (
                          <Button
                            type="button"
                            size="sm"
                            color="success"
                            className="btn-label"
                            onClick={() => setAction("approve")}
                          >
                            <i className="ri-check-line label-icon align-middle fs-14" />
                            Approve
                          </Button>
                        ) : null}
                        {(pending && (canReject || canDelete)) ||
                        (approved && canCancel) ? (
                          <UncontrolledDropdown>
                            <DropdownToggle
                              type="button"
                              className="btn btn-light btn-sm border"
                            >
                              <i className="ri-more-2-fill align-middle" />
                            </DropdownToggle>
                            <DropdownMenu end>
                              {pending && canReject ? (
                                <DropdownItem
                                  className="text-danger"
                                  onClick={() => setAction("reject")}
                                >
                                  <i className="ri-close-line align-bottom me-2" />
                                  Reject
                                </DropdownItem>
                              ) : null}
                              {pending && canDelete ? (
                                <DropdownItem
                                  className="text-danger"
                                  onClick={() => setAction("delete")}
                                >
                                  <i className="ri-delete-bin-line align-bottom me-2" />
                                  Delete
                                </DropdownItem>
                              ) : null}
                              {approved && canCancel ? (
                                <DropdownItem
                                  className="text-danger"
                                  onClick={() => setAction("cancel")}
                                >
                                  <i className="ri-forbid-line align-bottom me-2" />
                                  Cancel leave
                                </DropdownItem>
                              ) : null}
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </Row>

                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("1");
                      }}
                      href="#"
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <TabContent activeTab={activeTab} className="text-muted">
            <TabPane tabId="1">
              <OverviewTab />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default Section;
